import { useRef, useState } from "react";
import { toast } from "sonner";
import { trackPlausible } from "@/lib/plausible";
import { isCheckoutEnabled, WAITLIST_MAILTO } from "@/lib/checkout-config";
import { getStripeEnvironment } from "@/lib/stripe";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import type { CreateCheckoutSessionInput } from "@/lib/payments.functions";

export type OpenCheckoutOptions = {
  priceId: string;
  customerEmail?: string;
  userId?: string;
  returnUrl?: string;
  trialDays?: number;
  metadata?: Record<string, string>;
};

/**
 * Drives the embedded Stripe Checkout dialog. Replaces usePaddleCheckout.
 * Renders the dialog element directly (via `checkoutDialog`) — callers must
 * mount it once in their JSX tree. Never wrap it in a component factory;
 * that would remount EmbeddedCheckoutProvider on every render and drop the
 * client secret mid-flight.
 */
export function useStripeCheckout() {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState<CreateCheckoutSessionInput | null>(null);
  // Guard against double-clicks racing past setState's async flush.
  const inFlight = useRef(false);

  const openCheckout = async (options: OpenCheckoutOptions) => {
    if (inFlight.current) return;
    if (!isCheckoutEnabled()) {
      toast.info("Checkout opens soon — email hello@tack.company to join the waitlist.", {
        action: { label: "Join waitlist", onClick: () => window.open(WAITLIST_MAILTO, "_blank") },
      });
      return;
    }
    inFlight.current = true;
    trackPlausible("Upgrade Clicked", { price_id: options.priceId });

    setParams({
      priceId: options.priceId,
      customerEmail: options.customerEmail,
      userId: options.userId,
      returnUrl:
        options.returnUrl ||
        `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
      environment: getStripeEnvironment(),
      trialDays: options.trialDays,
      metadata: options.metadata,
    });
    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      inFlight.current = false;
    }
  };

  const checkoutDialog = (
    <CheckoutDialog open={open} onOpenChange={handleOpenChange} params={params} />
  );

  return { openCheckout, loading: open, checkoutDialog };
}
